import { useEffect, useRef, useState } from "react";
import { Button, useTheme } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/20/solid";
import useFocusTrap from "../../hooks/useFocusTrap";
import { validateEmail } from "../auth/validationHelpers";
import api from "../login/interceptors";
import { BASE_URL } from "../../Constants";

const EditCandidateModal = ({ isOpen, onClose, candidate, onSaved }) => {
  const theme = useTheme();
  const modalRef = useFocusTrap(isOpen, onClose);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const candidateRef = useRef(candidate);
  candidateRef.current = candidate;

  useEffect(() => {
    if (!isOpen) return;
    const current = candidateRef.current;
    setName(current?.name || "");
    setEmail(current?.email || "");
    setError("");
  }, [isOpen]);

  if (!isOpen) return null;

  const resetAndClose = () => {
    setError("");
    onClose();
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Name is required");
      return;
    }
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }
    const candidateId = candidate?.candidate_id;
    if (!candidateId) {
      setError("Could not update this candidate.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await api.patch(`${BASE_URL}/api/candidates/${candidateId}`, {
        name: trimmedName,
        email: email.trim(),
      });
      if (typeof onSaved === "function") await onSaved();
      onClose();
    } catch (err) {
      const data = err?.response?.data || {};
      setError(data.message || data.error || data.detail || "Could not update candidate.");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    borderColor: theme.palette.secondary.v4,
    color: theme.palette.text.main,
  };

  return (
    <div className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70">
      <div className="w-full flex justify-center items-start mt-[70px] px-4">
        <div
          ref={modalRef}
          className="modal-content relative w-full max-w-47rem bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-candidate-title"
        >
          <div
            className="modal-header flex justify-between items-center py-4 px-5 border-b"
            style={{
              backgroundColor: theme.palette.primary.main,
              borderColor: theme.palette.secondary.v4,
            }}
          >
            <h2 id="edit-candidate-title" className="font-semibold">
              Edit candidate
            </h2>
            <button
              onClick={resetAndClose}
              aria-label="Close modal"
              className="inline-flex items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="flex flex-col px-5 sm:px-6 py-6 gap-5">
            <div>
              <label
                htmlFor="candidate-name"
                className="block text-sm font-medium mb-2"
                style={{ color: theme.palette.text.main }}
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="candidate-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter full name"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={inputStyle}
                autoFocus
              />
            </div>
            <div>
              <label
                htmlFor="candidate-email"
                className="block text-sm font-medium mb-2"
                style={{ color: theme.palette.text.main }}
              >
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="candidate-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={inputStyle}
              />
            </div>
            {error && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
          </div>

          <div
            className="w-full py-3 px-5 flex justify-end border-t"
            style={{ borderColor: theme.palette.secondary.v4 }}
          >
            <Button
              onClick={handleSubmit}
              disabled={saving}
              sx={{
                backgroundColor: theme.palette.info.main,
                textTransform: "none",
                color: theme.palette.text.light,
              }}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCandidateModal;
